import { Paper, Popper, TextField } from '@material-ui/core';
import Downshift from 'downshift';
import React, { ChangeEvent, useCallback, useRef } from 'react';
import styled from 'styled-components';

import Skeleton from 'Elements/Loading/Skeleton';

import downshiftReducer from './downshiftReducer';
import Loader from './Loader';
import SearchResult from './SearchResult';
import Suggestions from './Suggestions';

const Scrollable = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

type Props<T> = {
  selectedItem: T | undefined;
  loading: boolean;
  onChange: (item: T | undefined) => void;
  itemToString: (item: T | undefined) => string;
  placeholder?: string;
  search: (query: string) => Promise<SearchResult<T>[]>;
  value: any;
  variant?: any;
  color?: any;
  size?: any;
  name: string;
  [key: string]: any;
};

export default function Autocomplete<T>({
  selectedItem,
  loading,
  itemToString,
  placeholder,
  search,
  onChange,
  name,
  value,
  ...props
}: Props<T>) {
  const inputRef = useRef(null);

  const handleChange = useCallback(
    (item?: SearchResult<T>) => {
      onChange(item ? item.payload : undefined);
    },
    [onChange]
  );

  if (value && selectedItem === undefined) {
    return (
      <Skeleton loading>
        <TextField {...props} />
      </Skeleton>
    );
  }

  return (
    <Downshift
      stateReducer={downshiftReducer}
      itemToString={itemToString}
      onChange={handleChange}
      selectedItem={selectedItem || null}
    >
      {({ getInputProps, getItemProps, inputValue, isOpen }) => (
        <div>
          <TextField
            {...getInputProps({
              ...props,
              ref: inputRef,
              type: 'search',
              onChange: (event: ChangeEvent<HTMLInputElement>) => {
                if (event.target.value === '') {
                  handleChange();
                }
              },
              placeholder: placeholder,
              InputProps: { endAdornment: loading && <Loader /> }
            })}
          />

          <Popper
            open={isOpen}
            anchorEl={inputRef.current}
            style={{
              minWidth: (inputRef.current || ({} as any)).clientWidth || 0,
              zIndex: 999
            }}
          >
            <Paper square elevation={3} component={Scrollable}>
              <Suggestions
                query={inputValue}
                getItemProps={getItemProps}
                search={search}
              />
            </Paper>
          </Popper>
        </div>
      )}
    </Downshift>
  );
}
